import {SyntheticEvent, useEffect, useMemo, useState} from 'react';
import {observer} from 'mobx-react';
import {useLocation, useNavigate} from 'react-router';
import {useQuery} from 'react-query';
import styled from 'styled-components';
import {useStoreContext} from '@store/store-context';
import {FlexRowSpaceBetween} from '@ui/style/styles';
import {Body1} from '@ui/atoms/typography';
import {VerticalSpacer} from '@ui/atoms/spacer';
import {EmptyPlaceholder} from '@ui/atoms/empty-placeholder';
import {Button} from '@ui/atoms/button';
import {getColorFromTheme} from '@ui/ui-utils';
import {cloudinary} from '@assets/images/cloudinary';
import {CompanyCheckinsController} from '../company-legacy/company-components/company-checkins/company-checkins.controller';
import {CompanyCheckinsRequestImpl} from '../company-legacy/company-components/company-checkins/company-checkins.request';
import {
  ICheckinCycle,
  ICheckinCyclesResponse,
} from '../company-legacy/company-components/company-checkins/company-checkins.interface';
import MyCheckins, {MyCheckinsTeamFeed} from './my_checkins/my_checkins';
import MyCheckinsHistory from './my_checkins/history-page';
import {CycleListTable} from './team-management/cycle-list-table';
import {ManagedCycle} from './team-management/team-management.utils';
import {TodoCountBadge} from './my_checkins/my_checkins-style';
import {canAdministerCheckins} from './checkins-access';
import {getActionablePendingCheckins} from './my_checkins/checkin-submit.utils';
import {
  FeaturePageContent,
  FeaturePageHeader,
} from '@ui/molecules/feature-page-header';

const CheckinsPageTitle = styled.h1`
  margin: 0;
  color: ${getColorFromTheme('blackTwo')};
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
`;

const CheckinsIntroRow = styled(FlexRowSpaceBetween)`
  gap: 24px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
    gap: 20px;

    > button {
      width: 100%;
    }
  }
`;

interface ICheckinsPage {
  tab?: 'questions' | 'team-management' | 'my-checkins' | 'history';
}

export const CheckinsPage = observer(({tab}: ICheckinsPage) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [myCheckinsView, setMyCheckinsView] = useState<
    'todo' | 'teamFeed' | 'history'
  >('todo');
  const {
    storeDashboardRoutes: {
      checkinsTabs,
      activateCheckinsTabs,
      activateDashboardTab,
    },
    authStore: {auth, setCalendarView},
    checkinsStore: {workspaceCheckinsList},
  } = useStoreContext();

  const request = useMemo(() => new CompanyCheckinsRequestImpl(), []);
  const controller = useMemo(() => new CompanyCheckinsController(request), [
    request,
  ]);
  const isAdmin = canAdministerCheckins(auth.user.role);
  const pendingTodoCount = useMemo(
    () => getActionablePendingCheckins(workspaceCheckinsList).length,
    [workspaceCheckinsList],
  );
  const requestedView = useMemo(
    () => new URLSearchParams(location.search).get('view'),
    [location.search],
  );

  const cyclesQuery = useQuery<
    ICheckinCyclesResponse | ICheckinCycle[] | false
  >(['check-in-cycles'], () => controller.getCheckinCycles(), {
    enabled: isAdmin,
    refetchOnWindowFocus: false,
  });
  const cycles = useMemo<ManagedCycle[]>(() => {
    const response = cyclesQuery.data;
    if (!response) return [];
    if (Array.isArray(response)) return response as ManagedCycle[];
    return (response.results || response.data || []) as ManagedCycle[];
  }, [cyclesQuery.data]);
  const hasNoCheckins =
    isAdmin && !cyclesQuery.isLoading && cycles.length === 0;
  const isManageCyclesPage =
    isAdmin && (tab === 'team-management' || checkinsTabs.direct_reports);

  const checkinsNavigation = useMemo(() => {
    if (hasNoCheckins) {
      return [
        {
          active: true,
          href: '/checkins',
          id: '1',
          onClick: (event: SyntheticEvent<HTMLAnchorElement>) =>
            event.preventDefault(),
          icon: null,
          disableHref: false,
          iconPosition: '',
          iconSpacing: '',
          title: 'Getting Started',
        },
      ];
    }
    const nav = [
      {
        active: checkinsTabs.my_checkins,
        href: '/checkins/my-checkins',
        id: '3',
        onClick: () => {
          activateCheckinsTabs('my_checkins');
          setCalendarView('month');
        },
        icon: null,
        disableHref: false,
        iconPosition: '',
        iconSpacing: '',
        title: 'Check-ins',
      },
    ];
    if (isAdmin) {
      nav.push({
        active: checkinsTabs.direct_reports,
        href: `/checkins/team-management${location.search}`,
        id: '2',
        onClick: () => {
          activateCheckinsTabs('direct_reports');
          setCalendarView('month');
        },
        icon: null,
        disableHref: false,
        iconPosition: '',
        iconSpacing: '',
        title: 'Manage Cycles',
      });
    }
    return nav;
  }, [
    activateCheckinsTabs,
    checkinsTabs.direct_reports,
    checkinsTabs.my_checkins,
    hasNoCheckins,
    isAdmin,
    location.search,
    setCalendarView,
  ]);

  const myCheckinsNavigations = useMemo(() => {
    const nav: any[] = [
      {
        active: myCheckinsView === 'todo',
        href: '#',
        id: 'my-checkins-todo',
        disableHref: true,
        onClick: (event: SyntheticEvent<HTMLAnchorElement>) => {
          event.preventDefault();
          setMyCheckinsView('todo');
        },
        icon: pendingTodoCount ? (
          <TodoCountBadge aria-label={`${pendingTodoCount} pending check-ins`}>
            {pendingTodoCount}
          </TodoCountBadge>
        ) : null,
        iconSpacing: '',
        title: 'To-Do',
      },
    ];
    nav.push({
      active: myCheckinsView === 'teamFeed',
      href: '#',
      id: 'my-checkins-team-feed',
      disableHref: true,
      onClick: (event: SyntheticEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        setMyCheckinsView('teamFeed');
      },
      iconSpacing: '',
      title: 'Team Feed',
    });
    nav.push({
      active: myCheckinsView === 'history',
      href: '#',
      id: 'my-checkins-history',
      disableHref: true,
      onClick: (event: SyntheticEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        setMyCheckinsView('history');
      },
      iconSpacing: '',
      title: 'History',
    });
    return nav;
  }, [myCheckinsView, pendingTodoCount]);

  useEffect(() => {
    activateDashboardTab('checkins');
    if (tab === 'team-management') {
      if (!isAdmin) {
        activateCheckinsTabs('my_checkins');
        navigate('/checkins/my-checkins', {replace: true});
      } else {
        activateCheckinsTabs('direct_reports');
        setCalendarView('month');
      }
    }
    if (tab === 'my-checkins') {
      activateCheckinsTabs('my_checkins');
      setCalendarView('month');
    }
    if (tab === 'history') {
      activateCheckinsTabs('my_checkins');
      setCalendarView('month');
      setMyCheckinsView('history');
    }
    if (requestedView === 'teamFeed' || requestedView === 'history') {
      activateCheckinsTabs('my_checkins');
      setMyCheckinsView(requestedView);
    }
  }, [
    activateCheckinsTabs,
    activateDashboardTab,
    isAdmin,
    navigate,
    requestedView,
    setCalendarView,
    tab,
  ]);

  const emptyPlaceholder = {
    image: {
      alt: 'No Check-in Cycle Yet',
      shadow: '-10px 44px 28px 0 rgba(19, 17, 17, 0.12)',
      src: cloudinary.emptyGoals,
      style: {height: '140px', width: '130px'},
    },
    action: (
      <div className="mt-4">
        <Button
          kind="primary"
          style={{padding: '8px 16px'}}
          onClick={() => navigate('/create-checkin-cycle')}>
          Create First Check-in Cycle
        </Button>
      </div>
    ),
    subtitle: (
      <div
        style={{
          whiteSpace: 'pre-line',
        }}>{`Create a custom routine with questions and pulse surveys—tailored to your team's needs.\n\n🗓️ Cadence (weekly, biweekly, monthly)\n💬 Questions to spark reflection\n📊 Pulse surveys for engagement`}</div>
    ),
    title: 'No Check-in Cycle Yet',
  };

  return (
    <>
      <FeaturePageHeader
        ariaLabel="Check-in views"
        width="wide"
        tabs={checkinsNavigation.map((item) => ({
          id: item.id,
          label: String(item.title),
          active: item.active,
          onClick: () => {
            item.onClick({preventDefault: () => undefined} as any);
            if (item.href !== '#') navigate(item.href);
          },
        }))}
      />
      <FeaturePageContent width="wide">
        <CheckinsIntroRow className="mb-6">
          <div>
            <CheckinsPageTitle>
              {isManageCyclesPage ? 'Manage Check-ins' : 'Check-ins'}
            </CheckinsPageTitle>
            <div className="mt-[6px] max-w-[800px]">
              <Body1 kind="textBody">
                {isManageCyclesPage
                  ? 'Create, schedule, and track check-in cycles for your team.'
                  : 'A shared space for your team to come together, share updates and reflect on what matters'}
              </Body1>
            </div>
          </div>
          {isManageCyclesPage && !hasNoCheckins && (
            <Button
              kind="primary"
              style={{height: '46px', padding: '12px 24px'}}
              onClick={() => navigate('/create-checkin-cycle')}>
              Create new cycle
            </Button>
          )}
        </CheckinsIntroRow>

        {hasNoCheckins && (
          <div className="rounded-[10px] border border-borderLight bg-white">
            <EmptyPlaceholder {...emptyPlaceholder} />
          </div>
        )}

        {!hasNoCheckins && checkinsTabs.my_checkins && (
          <>
            <div
              role="tablist"
              aria-label="My check-in sections"
              className="mb-6 flex w-fit items-center gap-1 rounded-[10px] bg-backgroundDark p-1">
              {myCheckinsNavigations.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={item.active}
                  onClick={item.onClick as any}
                  className={`flex min-h-[38px] items-center gap-2 rounded-[8px] px-4 text-sm font-medium ${
                    item.active
                      ? 'bg-white text-purple300 shadow-sm'
                      : 'text-textBody transition-colors hover:text-purple300'
                  }`}>
                  {item.title}
                  {item.icon}
                </button>
              ))}
            </div>
            {myCheckinsView === 'teamFeed' ? (
              <MyCheckinsTeamFeed />
            ) : myCheckinsView === 'history' ? (
              <MyCheckinsHistory />
            ) : (
              <MyCheckins />
            )}
          </>
        )}

        {!hasNoCheckins && checkinsTabs.direct_reports && isAdmin && (
          <CycleListTable cycles={cycles} isLoading={cyclesQuery.isLoading} />
        )}
        <VerticalSpacer size="80px" />
      </FeaturePageContent>
    </>
  );
});
